import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag, Checkbox } from '../../../../../../components';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../../utils';
import { YES_NO, TYPE_OF_CHARGES, PAYMENT_MODE, AVAILABLE_FOR, SERVICE_TYPE } from '../../../../../../constants';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../../components';
import { AddServicesDetailsForm } from './AddServicesDetailsForm';

const TABLE_COLUMNS = [
  {
    header: 'Service Type',
    accessor: 'type'
  },
  {
    header: 'Service  Name ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.service_name}`}
        </span>
      );
    }
  },

  {
    header: 'OutSourced',
    accessor: 'outsourced'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

const PACKAGE_TABLE_COLUMN = [
  {
    header: 'Service  Name ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.service_name || row?.service?.service_name}`}
        </span>
      );
    }
  }
];

const PACKAGE_TABLE_COLUMN_TEST = [
  {
    header: 'Test Name ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.service_name || row?.service?.test_name}`}
        </span>
      );
    }
  }
];

const initialServiceMasterDataObj = {
  data: [],
  loading: false
};

export const ServiceMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [serviceMasterDataObj, setServiceMasterDataObj] = useState(initialServiceMasterDataObj);
  const [serviceFilterData, setServiceFilterData] = useState(initialServiceMasterDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [groupDropdown, setGroupDropdown] = useState([]);
  const [subgroupDropdown, setSubgroupDropdown] = useState([]);
  const { pathname } = useLocation();
  const [input, setInput] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [originalData, setOriginalData] = useState(false);
  const [addServiceDialogue, setAddServiceDialogue] = useState(false);
  const [detailList, setDetailList] = useState([]);
  const [detailLabList, setDetailLabList] = useState([]);

  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        service_name: Yup.string().required('Service Name Is Required ')
      })
    ),
    defaultValues: {
      opd_charges: 0,
      ipd_charges: 0,
      min_charges: 0,
      max_charges: 0,
      free_visit: 0,
      indoor: 0,
      lab: 0
    },
    mode: 'all'
  });
  const { handleSubmit, setValue, reset, watch } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'status', '__v']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('default', selectedRecord.status === 'default');
      setDetailList(selectedRecord?.service_items);
      setDetailLabList(selectedRecord?.lab_items);
    } else {
      reset();
    }
  }, [setValue, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/service-update' : '/service-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...formValues,
        service_items: detailList,
        lab_items: detailLabList,
        status: formValues.default ? 'default' : 'active'
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} Service master`, { variant: 'success' });
      setSelectedRecord(response?.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} Service master`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/service-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully Cancelled Service master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to Cancelled Service master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/service-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Service master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Service master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setServiceMasterDataObj({ loading: true });
    const response = await request('/service-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setServiceMasterDataObj({ loading: false, data: filteredData });
      setServiceFilterData({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setServiceMasterDataObj({ loading: false });
    }
  };

  const getsubgroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('subgroup-master');
    if (!selectedRecord) {
      const [defaultState] = data.filter(({ status }) => status === 'default');
      setValue('sub_group', defaultState?._id);
    }
    setSubgroupDropdown(data);
  }, []);

  const getGroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('group-master');
    if (!selectedRecord) {
      const [defaultGroup] = data.filter(({ status }) => status === 'default');
      setValue('main_group', defaultGroup?._id);
    }
    setGroupDropdown(data);
  }, []);

  useEffect(() => {
    getGroupDropdown();
    getsubgroupDropdown();
  }, [getGroupDropdown, getsubgroupDropdown]);

  useEffect(() => {
    if (serviceMasterDataObj.data && serviceMasterDataObj.data.length > 0) {
      setOriginalData(serviceMasterDataObj.data);
      setIsFiltered(false);
    }
  }, [serviceMasterDataObj.data]);

  useEffect(() => {
    handleServiceSearch();
  }, [input]);

  const handleServiceSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();

    try {
      const response = await request('/service-list', {
        method: 'GET',
        query: { service_name: lowercasedInput }
      });
      if (response.success && Array.isArray(response.data)) {
        setServiceMasterDataObj({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setServiceMasterDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setServiceMasterDataObj({ loading: false, data: [] });
    }
  };

  const handleInputChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  const ServiceTypeWatch = watch('type');

  const onDetailAddHandler = (data) => {
    const existingDetail = detailList?.find(({ service }) => service === data?.service);

    if (existingDetail) {
      toast('Service already chosen', { variant: 'error' });
      return;
    }

    const serviceName = data?.serviceName;

    if (data?.type === 'L') {
      setDetailLabList((prev) => [
        ...prev,
        {
          ...data,
          service_name: serviceName
        }
      ]);
      toast('Service Added to Lab Successfully', { variant: 'success' });
    } else {
      setDetailList((prev) => [
        ...prev,
        {
          ...data,
          service_name: serviceName
        }
      ]);
      toast('Service Added Successfully', { variant: 'success' });
    }
  };

  const handleRowDelete = (rowId, type) => {
    if (type === 'L') {
      setDetailLabList((prev) => {
        const updatedList = prev.filter((data) => {
          return data.service !== rowId;
        });

        return updatedList;
      });
    } else {
      setDetailList((prev) => {
        const updatedList = prev.filter((data) => {
          return data.service !== rowId;
        });

        return updatedList;
      });
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Service Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={handleSubmit(formSubmit)}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={5}>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, marginTop: '10px' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service Name </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      name="service_name"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service Code </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      name="service_code"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service Type</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      name="type"
                      menus={SERVICE_TYPE}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Main Group</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      name="main_group"
                      menus={groupDropdown.map((data) => ({ ...data, name: data.groupname }))}
                    />
                  </Stack>

                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sub Group</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="sub_group"
                      menus={subgroupDropdown.map((data) => ({ ...data, name: data.subgroupname }))}
                    />
                  </Stack>

                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.5, fontWeight: 700, fontSize: '1.4rem' }}>Opd/Ipd Charges</Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 1, border: currentAction ? '0.5px solid red' : '' }}
                      name="opd_charges"
                      type="number"
                    />

                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 1, border: currentAction ? '0.5px solid red' : '' }}
                      type="number"
                      name="ipd_charges"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.21, fontWeight: 700, fontSize: '1.4rem' }}>Payment Mode</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.5 }}
                      name="payment_mode"
                      menus={PAYMENT_MODE}
                    />
                    <Typography sx={{ flex: 0.25, fontSize: '1.4rem', fontWeight: 700 }}>Default</Typography>
                    <Checkbox disabled={!['new', 'edit'].includes(currentAction)} name="default" fontSize={18} />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            {ServiceTypeWatch === 'Pc' && (
              <Stack spacing={2} direction="column" alignItems="flex-start">
                <Button variant="contained" onClick={() => setAddServiceDialogue(true)}>
                  Add Services
                </Button>
                <Table
                  columns={PACKAGE_TABLE_COLUMN}
                  data={(detailList && detailList.map((data) => ({ _id: data.service, ...data }))) || []}
                  showDeleteAction={true}
                  onDeleteHandler={handleRowDelete}
                  loading={originalData.loading}
                />
              </Stack>
            )}
            {ServiceTypeWatch === 'Pc' && (
              <Stack spacing={2} direction="column" alignItems="flex-start">
                <Table
                  columns={PACKAGE_TABLE_COLUMN_TEST}
                  data={(detailLabList && detailLabList.map((data) => ({ _id: data.service, ...data }))) || []}
                  showDeleteAction={true}
                  onDeleteHandler={handleRowDelete}
                  loading={originalData.loading}
                />
              </Stack>
            )}
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 0,
            borderBottom: '0px solid',
            borderColor: 'grey.300'
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ fontWeight: 600 }}>
            Service Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' // Adjust input font size
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
                // fontSize: '14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Service"
            variant="outlined"
            value={input}
            onChange={handleInputChange}
            InputProps={{
              style: {
                fontSize: '12px',
                fontWeight: 600
              }
            }}
          />
        </Stack>
        <Table
          sx={{ mt: 1 }}
          columns={TABLE_COLUMNS}
          data={originalData || []}
          loading={originalData.loading}
          onRowClick={(row) => {
            setOpenQueryDialog(false);
            setSelectedRecord(row);
          }}
        />
      </Dialog>

      <Dialog
        open={addServiceDialogue}
        onClose={() => setAddServiceDialogue(false)}
        PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <AddServicesDetailsForm
          chargeType="ipd_charges"
          onClose={() => setAddServiceDialogue(false)}
          onAdd={onDetailAddHandler}
        />
      </Dialog>
    </>
  );
};
